import cx from 'clsx';
import { Splide, SplideSlide } from '@splidejs/react-splide';
import { useLocalization } from '@fluent/react';
import { useState } from 'react';

import SignUpButton from './SignUpButton';
import clientEasyBookingIcon from '#/assets/static-page/client_easy_booking_icon.svg';
import clientEasyBookingImg from '#/assets/static-page/client_easy_booking.png';
import clientInstantDeliveryIcon from '#/assets/static-page/client_instant_delivery_icon.svg';
import clientInstantDeliveryImg from '#/assets/static-page/client_instant_delivery.png';
import clientLiveTrackingImg from '#/assets/static-page/client_live_tracking.png';
import clientSwiftPaymentImg from '#/assets/static-page/client_swift_payment.png';
import paymentIcon from '#/assets/static-page/client_swift_payment_icon.svg';
import trackingIcon from '#/assets/static-page/client_live_tracking_icon.svg';
import transporterAutoPaymentsImg from '#/assets/static-page/transporter_auto_payments.png';
import transporterClienteleAccessIcon from '#/assets/static-page/transporter_clientele_access_icon.svg';
import transporterClienteleAccessImg from '#/assets/static-page/transporter_clientele_access.png';
import transporterEasyDeliveryImg from '#/assets/static-page/transporter_easy_delivery.png';
import transporterInstantAcceptanceIcon from '#/assets/static-page/transporter_instant_acceptance_icon.svg';
import transporterInstantAcceptanceImg from '#/assets/static-page/transporter_instant_acceptance.png';
import { PublicRoute } from '#/constants/page-route';

const HomePageBenefitSection = () => {
  const { l10n } = useLocalization();
  const [isClientSelected, setIsClientSelected] = useState(true);

  const handleIsClientSelected = (selected: boolean) => {
    setIsClientSelected(selected);
  };

  return (
    <div className="flex flex-col py-16 bg-white">
      <div className="flex justify-center flex-1">
        <div className="flex flex-col gap-y-5">
          <h2 className="font-light text-center text-blue-700">It's time to MOVE forward.</h2>
          <div className="text-gray-400">
            By simplifying the process with our platform, you can complete a delivery virtually and
            efficiently
          </div>
        </div>
      </div>
      <div className="py-10">
        <div className="relative z-10 flex flex-col items-center gap-y-10 ">
          <div className="flex p-3 rounded-full w-80 bg-blue-50">
            <button
              className={cx(
                'py-2 rounded-full w-40 hover:cursor-pointer',
                isClientSelected ? 'bg-blue-700 text-white' : 'text-black',
              )}
              onClick={() => handleIsClientSelected(true)}
            >
              <div>For Clients</div>
            </button>
            <button
              className={cx(
                'py-2 rounded-full w-40 hover:cursor-pointer',
                isClientSelected ? 'text-black' : 'bg-blue-700 text-white',
              )}
              onClick={() => handleIsClientSelected(false)}
            >
              <div>For Transporters</div>
            </button>
          </div>
          <div className="w-full 2xl:flex 2xl:justify-center">
            {isClientSelected ? (
              <Splide options={{ arrows: false, autoWidth: true, pagination: false }}>
                <SplideSlide>
                  <BenefitCard
                    icon={clientEasyBookingIcon}
                    image={clientEasyBookingImg}
                    isClient={isClientSelected}
                    step={1}
                    text="After entering the required details, the platform will provide the appropriate transportation service for your goods."
                    title={l10n.getString('easy-booking')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={clientInstantDeliveryIcon}
                    image={clientInstantDeliveryImg}
                    isClient={isClientSelected}
                    step={2}
                    text="With just a few clicks, you will receive a job acceptance from our pool of reliable transporters to carry out the delivery."
                    title={l10n.getString('instant-delivery')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={trackingIcon}
                    image={clientLiveTrackingImg}
                    isClient={isClientSelected}
                    step={3}
                    text="We minimise uncertainty, miscommunications and mishaps by giving you updates and live access to monitor your goods’ status."
                    title={l10n.getString('live-tracking')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={paymentIcon}
                    image={clientSwiftPaymentImg}
                    isClient={isClientSelected}
                    step={4}
                    text="Upon job completion, all you have to do is confirm the delivery and the platform will automatically handle the payment."
                    title={l10n.getString('swift-payments')}
                  />
                </SplideSlide>
              </Splide>
            ) : (
              <Splide options={{ arrows: false, autoWidth: true }}>
                <SplideSlide>
                  <BenefitCard
                    icon={transporterClienteleAccessIcon}
                    image={transporterClienteleAccessImg}
                    isClient={isClientSelected}
                    step={1}
                    text="After signing up, the platform will provide you with clients that require your transportation service for their goods."
                    title={l10n.getString('clientele-access')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={transporterInstantAcceptanceIcon}
                    image={transporterInstantAcceptanceImg}
                    isClient={isClientSelected}
                    step={2}
                    text="With just a few clicks, transporters will be prompted with several job requests from our clientele to carry out their delivery."
                    title={l10n.getString('instant-acceptance')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={trackingIcon}
                    image={transporterEasyDeliveryImg}
                    isClient={isClientSelected}
                    step={3}
                    text="We minimise uncertainty, miscommunications and mishaps by providing clients with live tracking and updates so you can focus on delivery."
                    title={l10n.getString('easy-delivery')}
                  />
                </SplideSlide>
                <SplideSlide>
                  <BenefitCard
                    icon={paymentIcon}
                    image={transporterAutoPaymentsImg}
                    isClient={isClientSelected}
                    step={4}
                    text="Upon job completion, the platform will automatically pay you for your services and continue to pull in clients for you."
                    title={l10n.getString('auto-payments')}
                  />
                </SplideSlide>
              </Splide>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center justify-center flex-1 gap-x-10">
        <div className="text-lg text-blue-700">Logistics in the palm of your hands</div>
        <SignUpButton
          className="mt-0 bg-blue-700"
          iconClassName="text-white"
          text={l10n.getString('sign-up-now')}
          textClassName="text-white"
          route={isClientSelected ? PublicRoute.CustomerSignup : PublicRoute.LogisticsSignup}
        />
      </div>
    </div>
  );
};

const BenefitCard: React.FC<BenefitCardProps> = ({ icon, image, isClient, step, text, title }) => {
  return (
    <div className="">
      <div className="relative px-8">
        <div className="relative z-10 space-y-5">
          <div className="flex justify-center">
            <div className="inline-flex items-center justify-center w-8 h-8 text-lg text-white rounded-full bg-primary-400">
              {step}
            </div>
          </div>
          <div className="flex items-center justify-center pt-3 gap-x-5">
            <img src={icon} className="w-8 h-8" />
            <h4 className="text-blue-700">{title}</h4>
          </div>
        </div>
        <div
          className={cx(
            'absolute top-3 left-[-50%] right-[50%] h-2 bg-primary-400 z-0',
            // FIXME: more elegant solution
            step === 1 && 'left-[50%]',
          )}
        ></div>
      </div>
      <div className="p-8 w-96">
        <div className={cx('rounded-t-3xl px-10', isClient ? 'bg-blue-700' : 'bg-blue-50')}>
          <img src={image} />
        </div>
        <div className={cx('rounded-b-3xl', isClient ? 'bg-blue-50' : 'bg-blue-700')}>
          <p
            className={cx(
              'px-6 py-8 max-w-sm text-lg h-48',
              isClient ? 'text-blue-700' : 'text-white',
            )}
          >
            {text}
          </p>
        </div>
      </div>
    </div>
  );
};
export type BenefitCardProps = {
  icon?: string;
  image?: string;
  isClient: boolean;
  step: number;
  text: string;
  title: string;
};

export default HomePageBenefitSection;
